import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { remindersManager } from '../../utils/remindersData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';

const FlowsViewAll = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modalConfig, showConfirm, closeModal } = useModal();
  
  const [workflows, setWorkflows] = useState([]);
  const [customWorkflows, setCustomWorkflows] = useState([]);
  const [activeTab, setActiveTab] = useState('song-release'); // song-release, show, custom

  useEffect(() => {
    loadAllWorkflows();
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      loadAllWorkflows();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleStorageChange);
    };
  }, []);

  const loadAllWorkflows = () => {
    try {
      remindersManager.reloadReminders();
      
      const allReminders = remindersManager.getAllReminders();
      
      // Group by workflow
      const songReleases = {};
      const shows = {};
      const custom = {};
      
      allReminders.forEach(reminder => {
        if (reminder.metadata?.quick_reminder || reminder.templateId === 'CUSTOM') {
          // Skip quick reminders - those are handled separately
          return;
        }
        
        if (reminder.metadata?.type === 'booking') {
          const id = reminder.releaseId;
          if (!shows[id]) {
            shows[id] = {
              id,
              name: reminder.releaseName,
              date: reminder.releaseDate,
              type: 'show',
              steps: [],
              completedSteps: 0,
              totalSteps: 0
            };
          }
          shows[id].steps.push(reminder);
          shows[id].totalSteps++;
          if (reminder.status === 'completed') {
            shows[id].completedSteps++;
          }
        } else if (reminder.metadata?.custom) {
          const id = reminder.metadata?.workflowId || reminder.releaseId;
          if (!custom[id]) {
            custom[id] = {
              id,
              name: reminder.releaseName,
              date: reminder.releaseDate,
              type: 'custom',
              steps: [],
              completedSteps: 0,
              totalSteps: 0
            };
          }
          custom[id].steps.push(reminder);
          custom[id].totalSteps++;
          if (reminder.status === 'completed') {
            custom[id].completedSteps++;
          }
        } else {
          const id = reminder.releaseId;
          if (!songReleases[id]) {
            songReleases[id] = {
              id,
              name: reminder.releaseName,
              date: reminder.releaseDate,
              type: 'song-release',
              steps: [],
              completedSteps: 0,
              totalSteps: 0
            };
          }
          songReleases[id].steps.push(reminder);
          songReleases[id].totalSteps++;
          if (reminder.status === 'completed') {
            songReleases[id].completedSteps++;
          }
        }
      });
      
      setWorkflows({
        'song-release': Object.values(songReleases).sort((a, b) => new Date(a.date) - new Date(b.date)),
        'show': Object.values(shows).sort((a, b) => new Date(a.date) - new Date(b.date)),
        'custom': Object.values(custom).sort((a, b) => new Date(a.date) - new Date(b.date))
      });
      
    } catch (error) {
      console.error('Error loading workflows:', error);
      dispatch(setErrorAlert('Failed to load workflows'));
    } finally {
    }
  };

  const getCompletionPercentage = (workflow) => {
    if (workflow.totalSteps === 0) return 0;
    return Math.round((workflow.completedSteps / workflow.totalSteps) * 100);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleEdit = (workflow) => {
    navigate(`/flows/workflow/${workflow.id}`);
  };

  const handleDelete = (workflow) => {
    showConfirm(
      'Delete Workflow',
      `Are you sure you want to delete "${workflow.name}"? This will remove all associated reminders.`,
      () => {
        try {
          // Delete all reminders for this workflow
          remindersManager.reminders = remindersManager.reminders.filter(r => r.releaseId !== workflow.id);
          remindersManager.saveReminders();
          
          dispatch(setSuccessAlert('Workflow deleted successfully'));
          loadAllWorkflows();
        } catch (error) {
          console.error('Error deleting workflow:', error);
          dispatch(setErrorAlert('Failed to delete workflow'));
        }
      },
      'warning',
      'Delete',
      'Cancel'
    );
  };

  const getCurrentWorkflows = () => {
    return workflows[activeTab] || [];
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] min-h-screen flex flex-col items-center relative">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>All Workflows</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => {
                const canGoBack = window.history.length > 1;
                if (canGoBack) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20">
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              <button
                onClick={() => setActiveTab('song-release')}
                className={`px-4 py-2 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all ${
                  activeTab === 'song-release'
                    ? 'bg-[#AAFF00] text-white'
                    : 'bg-[rgba(255,255,255,0.05)] text-white opacity-50 hover:opacity-100'
                }`}
              >
                Song Releases
              </button>
              <button
                onClick={() => setActiveTab('show')}
                className={`px-4 py-2 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all ${
                  activeTab === 'show'
                    ? 'bg-[#AAFF00] text-white'
                    : 'bg-[rgba(255,255,255,0.05)] text-white opacity-50 hover:opacity-100'
                }`}
              >
                Shows
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={`px-4 py-2 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all ${
                  activeTab === 'custom'
                    ? 'bg-[#AAFF00] text-white'
                    : 'bg-[rgba(255,255,255,0.05)] text-white opacity-50 hover:opacity-100'
                }`}
              >
                Custom
              </button>
            </div>

            {/* Workflows List */}
            {getCurrentWorkflows().length === 0 ? (
              <div className="text-center py-12">
                <p className="text-white text-[14px] opacity-50 mb-4">No {activeTab.replace('-', ' ')} workflows yet</p>
                <button
                  onClick={() => navigate('/flows/create-workflow')}
                  className="bg-[#AAFF00] text-white text-[12px] font-medium px-6 py-3 rounded-lg hover:bg-[rgba(170,255,0,0.9)] transition-all"
                >
                  Create Workflow
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {getCurrentWorkflows().map((workflow) => (
                  <div
                    key={workflow.id}
                    className="w-full bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#AAFF00] transition-all"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-white text-[12px] font-normal">
                            {workflow.name}
                          </h3>
                          <button
                            onClick={() => handleEdit(workflow)}
                            className="text-[#AAFF00] text-[10px] px-2 py-1 hover:bg-[rgba(170,255,0,0.1)] rounded transition-colors"
                          >
                            View
                          </button>
                        </div>
                        <p className="text-white text-[10px] opacity-50 mb-2">
                          {workflow.type === 'show' ? 'Show: ' : workflow.type === 'custom' ? 'Custom: ' : 'Release: '}
                          {formatDate(workflow.date)}
                        </p>
                        
                        {/* Progress */}
                        <div className="mb-2">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-white text-[10px] opacity-70">
                              {workflow.completedSteps}/{workflow.totalSteps} steps
                            </span>
                            <span className="text-[#AAFF00] text-[10px] font-semibold">
                              {getCompletionPercentage(workflow)}% Complete
                            </span>
                          </div>
                          <div className="w-full bg-[rgba(255,255,255,0.1)] rounded-full h-1.5">
                            <div 
                              className="bg-[#AAFF00] h-1.5 rounded-full transition-all"
                              style={{ width: `${getCompletionPercentage(workflow)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(workflow)}
                        className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[10px] py-2 rounded hover:border-[#AAFF00] transition-all"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleDelete(workflow)}
                        className="flex-1 bg-[rgba(170,255,0,0.1)] border border-[rgba(170,255,0,0.3)] text-[#AAFF00] text-[10px] py-2 rounded hover:bg-[rgba(170,255,0,0.2)] transition-all"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default FlowsViewAll;

